
import {Component} from "./Component";
import {Utils} from "../common/Utils";
import {DataList} from "../data";

const DYNAMIC_STATE_LOADING = 1;
const DYNAMIC_STATE_READY = 2;

export class Form extends Component {

  constructor(config) {

    /**
     *
     * @type {{validate: {validClass: string, focusInvalid: boolean, errorElement: string, errorClass: string, ignore: string, invalidClass: string}}}
     */
    let defaultConf = {
      validate: {
        validClass: 'is-valid',
        invalidClass: 'is-invalid',
        errorClass: 'invalid-feedback',
        errorElement: 'div',
        focusInvalid: true,
        ignore: ":not(input[type=radio]):not(input[type=checkbox]):not(.validate-hidden):hidden, .ignore-validation:hidden",
      }
    };

    let defaultFormEl = 'form';
    if (config.el && $(config.el).prop('tagName').toLowerCase() === 'form') {
      defaultFormEl = config.el;
    }
    let autoInit = config.autoInit !== false;

    super({
      autoInit: false,
      dynamic: {},
      ui: {
        errorContainer: defaultFormEl,
        form: defaultFormEl,
        input: ':input',
        submitButton: 'button[type=submit]'
      },
      events: {
        'reset @form': 'resetHandler'
      },
      renderer: {
        select: 'renderSelect',
        radio: 'renderRadio',
        checkbox: 'renderCheckbox',
      }
    }, config);

    let conf = $.extend(true, defaultConf, config);
    this.validateConf = conf.validate;
    this._formError = null;
    this.inputFieldMap = {};
    this.dynamicField = {};

    if (autoInit) {
      this.init();
    }
  }

  /**
   * Build an internal map with references to all input fields by its names.
   * @private
   */
  _findInputs() {
    // get all input fields and store by name
    this.ui.input = this.el.find(this._ui.input);
    this.ui.input.each((i, el) => {
      let $el = $(el);
      let name = $el.prop('name');
      if (!name) {
        return;
      }
      this.inputFieldMap[name] = this.inputFieldMap[name] ? this.inputFieldMap[name].add($el) : $el;
    });
  }

  onInit() {

    this._findInputs();

    // find dynamic fields and load data
    // TODO: really necessary anymore?
    for (let key in this.config.dynamic) {
      if (this.config.dynamic.hasOwnProperty(key) && this.inputFieldMap[key]) {

        let opt = {};
        let type = this.config.dynamic[key];
        let labelProp = 'label';
        if (!Utils.isString(type)) {
          opt = type;
          type = opt.type;
          labelProp = opt.labelProperty || labelProp;
        }

        let collection = new DataList(type);
        this.dynamicField[key] = {
          state: DYNAMIC_STATE_LOADING,
          collection: collection
            .fetch(opt)
            .then(() => this.renderDynamicInput(key, labelProp, collection))
            .then(() => {
              this.dynamicField[key].state = DYNAMIC_STATE_READY;
            })
        };
      }
    }

    // bind jquery validate functions to this class
    ['invalidHandler', 'errorPlacement', 'highlight', 'unhighlight', {name: 'submitHandler', fn: '_submit'}]
      .forEach((fn) => {
        if (Utils.isString(fn)) {
          this.validateConf[fn] = this[fn].bind(this);
        }
        else {
          this.validateConf[fn.name] = this[fn.fn].bind(this);
        }
      });

    this.validator = this.ui.form.validate(this.validateConf);
  }

  /**
   * Callback to be triggered on error. Normally nothing must be done.
   * @param {Event} event - origin submit event
   * @param validator - jquery validator instance
   */
  invalidHandler(event, validator){
    event.preventDefault();
  }

  /**
   * Callback that place the given error to the related input element.
   * @param {jQuery} error - jQuery DOM element that contains the error message
   * @param {jQuery} element - input field that triggers the error
   */
  errorPlacement(error, element) {
    switch(element.prop("type")) {
      case 'checkbox':
        // checkbox group -> append error to form group
        if (this.validator.findByName(element.attr('name')).length > 1) {
          element.parent().parent().append(error);
        }
        else {
          element.parent().append(error);
        }
        break;
      case 'radio':
        element.parent().parent().append(error);
        break;
      case 'file':
        element.parent().parent().append(error);
        break;
      default:
        element.parent().append(error);
    }
  }

  /**
   * Mark the given input field as invalid.
   * @param {HTMLElement} element - DOM element to be marked
   * @param {String} errorClass - error css class name (for wrapper)
   * @param {String} validClass - success css class name (for input)
   */
  highlight(element, errorClass, validClass) {
    if (!element) return;

    let $el;
    if (element.type && (element.type === "radio" || element.type === "checkbox")) {
      $el = this.validator.findByName(element.name);
    }
    else {
      $el = $(element);
    }
    $el.addClass(this.validateConf.invalidClass);
    if (validClass) {
      $el.removeClass(validClass);
    }
  }

  /**
   * Mark the given input field as valid.
   * @param {HTMLElement} element - DOM element to be marked
   * @param {String} errorClass - error css class name (for wrapper)
   * @param {String} validClass - success css class name (for input)
   */
  unhighlight(element, errorClass, validClass) {
    if (!element) return;

    let $el;
    if (element.type && (element.type === "radio" || element.type === "checkbox")) {
      $el = this.validator.findByName(element.name);
    }
    else {
      $el = $(element);
    }
    if (validClass) {
      $el.addClass(validClass);
    }
    $el.removeClass(this.validateConf.invalidClass);
  }

  /**
   * Remove global form error if exists
   * @param form
   * @param event
   * @private
   */
  _submit(form, event) {
    event.preventDefault();
    this.submitHandler(form, event);
  }

  /**
   * To be called after successful validation. Should be implemented by
   * child class.
   * @param form
   * @param event
   */
  submitHandler(form, event) {
    this.detachFormError();
    this.emit('submit', this, form);
  }

  /**
   * Callback if reset button/action was triggered onto the form.
   * @param {Event} e
   */
  resetHandler(e) {
    this.triggerMethodOn('reset', [this]);
  }

  /**
   * Reset manually the form.
   */
  reset() {
    this.ui.form[0].reset();
  }

  /**
   * Build the DOM for the error message.
   * @param {String} message - error message to be displayed
   * @return {String} html markup
   */
  getErrorTpl(message) {
    return `<div class="alert alert-danger" role="alert">
      ${message}
    </div>`;
  }

  /**
   * Add the given error message to the form.
   * @param message
   */
  setFormError(message) {
    this._formError = $($.parseHTML(this.getErrorTpl(message)));
    this.attachFormError();
  }

  /**
   * Attach error DOM element to the form.
   */
  attachFormError() {
    this.ui.errorContainer.prepend(this._formError);
  }

  /**
   * Remove error message from form.
   */
  detachFormError() {
    this._formError && this._formError.remove();
    this._formError = null;
  }

  /**
   * Populate the given key-value data map to the form, where the key
   * is the input field name.
   * @param {Object} data
   * @return {Form}
   */
  setFormData(data) {
    for (let name in this.inputFieldMap) {

      let $el = this.inputFieldMap[name];
      if (!$el || !data.hasOwnProperty(name)) {
        continue;
      }

      if (this.dynamicField[name] && this.dynamicField[name].state === DYNAMIC_STATE_LOADING) {
        this.dynamicField[name].value = data[name];
        continue;
      }

      this.setValue(name, data[name]);
    }

    return this;
  }

  /**
   * Set a value to the input element with the given name.
   * @param name
   * @param value
   */
  setValue(name, value) {
    let $el = this.inputFieldMap[name];
    if (value === null) {
      return this;
    }

    let type = this.getInputType(name);

    switch (type) {

      // server/API date format vs. input date
      case 'date':
        $el.val(Utils.extractDate(value));
        break;

      // single checkbox, boolean value
      case 'checkbox':
        $el.prop('checked', !!value);
        break;

      // checkbox group with multiple values
      case 'checkbox-group':
        if (!Array.isArray(value)) {
          value = [value];
        }
        value.forEach((value) => {
          $el.filter('[value="' + value + '"]').prop('checked', true);
        });
        break;

      // radio group
      case 'radio':
        if (Utils.isBool(value)) {
          value = value ? 'true' : 'false';
        }
        $el.filter('[value="' + value + '"]').prop('checked', true);
        break;

      // file input doesn't support setting value
      case 'file':
        $el.data('value', value);
        break;

      // TODO: time

      default:
        // special handling for boolean (also in {@link parseValue})
        if (Utils.isBool(value)) {
          value = value ? "true" : "false";
        }
        $el.val(value);
    }
  }

  /**
   * Parse the given value into a JavaScript related object based
   * on the type, determined by {@link getInputType}.
   * @param {String} inputType - description of the input field
   * @param {*} value - value to be parsed (from the input field)
   * @return {*}
   */
  parseValue(inputType, value) {

    value = value.trim();
    if (value === "") {
      return null;
    }

    // check boolean values
    if (value === "true") return true;
    if (value === "false") return false;

    switch(inputType) {

      case 'date':
        value += 'T00:00:00.000+0000';
        break;

      case 'checkbox-group':
        if (!Array.isArray(value)) {
          value = [ value ];
        }
        break;

      // TODO: number -> float or integer
    }

    return value;
  }

  /**
   * Determines a type name from the given input name. The type name
   * comes from the tagName (select fields) or type attribute.
   * Supports multiple value elements: "select-multi" and "checkbox-group"
   * Returns null, if the input field could not be find.
   *
   * @param {String} name - input field name
   * @return {string|null} - input type name
   */
  getInputType(name) {

    if (!this.inputFieldMap[name]) {
      return null;
    }

    let $el = this.inputFieldMap[name];
    let type = ($el.attr('type') || $el.prop('tagName')).toLowerCase();

    switch(type) {
      case 'select':
        return type + ($el.prop('multiple') ? '-multi' : '');

      case 'checkbox':
        return type + ($el.length > 1 ? '-group' : '');

      case 'number':
      case 'file':
      case 'date':
      case 'radio':
        return type;

      default:
        return 'text';
    }
  }

  /**
   * Returns a key-value object with the given data from all input fields,
   * where the key is the input name.
   *
   */
  getFormData() {
    let prop = {};
    let data = this.ui.form.serializeArray();

    // TODO: rethink, because unchecked checkbox needs "false" boolean value
    data.forEach((input) => {

      if (!prop[input.name]) {
        prop[input.name] = this.parseValue(this.getInputType(input.name), input.value);
      }
      else {
        let v = prop[input.name];
        if (!Array.isArray(v)) {
          v = [v];
        }
        v.push(input.value);
        prop[input.name] = v;
      }
    });
    return prop;
  }
}
